import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { useUser } from '../context/UserContext';
import {
  cargarMetaVolumenMensual,
  getSeguimientoEjecutivoCharts,
  getSeguimientoEjecutivoKpis,
  getSeguimientoEjecutivoTables,
} from '../api/apiConfig';

const currentMonth = () => {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${d.getFullYear()}-${month}`;
};

const normalizeHeader = (v) => String(v || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toUpperCase()
  .replace(/[_\-.]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const findHeader = (headers, aliases) => {
  const set = new Set(aliases.map(normalizeHeader));
  return headers.find((h) => set.has(normalizeHeader(h))) || null;
};

const parseCsvSemicolon = (text) => {
  const lines = String(text || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .filter((line) => line.trim() !== '');
  if (!lines.length) return [];

  const headers = lines[0].split(';').map((h) => h.trim());
  const rows = [];
  for (let i = 1; i < lines.length; i += 1) {
    const parts = lines[i].split(';');
    const row = {};
    headers.forEach((h, idx) => {
      row[h] = (parts[idx] ?? '').trim();
    });
    rows.push(row);
  }
  return rows;
};

const toNumber = (v) => {
  if (v == null || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;

  let raw = String(v).trim().replace(/\s+/g, '');
  if (!raw) return null;

  const hasDot = raw.includes('.');
  const hasComma = raw.includes(',');

  if (hasDot && hasComma) {
    // Usa el separador que aparece al final como decimal.
    if (raw.lastIndexOf(',') > raw.lastIndexOf('.')) {
      // 1.234.567,89 -> 1234567.89
      raw = raw.replace(/\./g, '').replace(',', '.');
    } else {
      // 1,234,567.89 -> 1234567.89
      raw = raw.replace(/,/g, '');
    }
  } else if (hasComma) {
    if ((raw.match(/,/g) || []).length > 1) {
      // 1,234,567 -> 1234567
      raw = raw.replace(/,/g, '');
    } else {
      // 1234567,89 -> 1234567.89
      raw = raw.replace(',', '.');
    }
  } else if (hasDot) {
    if ((raw.match(/\./g) || []).length > 1) {
      // 1.234.567 -> 1234567
      raw = raw.replace(/\./g, '');
    }
  }

  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

const CargaMetaVolumenMensual = () => {
  const APPLY_CHUNK_SIZE = 8;
  const { user } = useUser();
  const [month, setMonth] = useState(currentMonth());
  const [nombreCam, setNombreCam] = useState('');
  const [rows, setRows] = useState([]);
  const [invalidRows, setInvalidRows] = useState([]);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [utilsVersions, setUtilsVersions] = useState({
    loading: false,
    kpis: 'N/D',
    charts: 'N/D',
    tables: 'N/D',
    error: '',
  });

  const canSubmit = useMemo(() => !!user && rows.length > 0 && !loading, [user, rows.length, loading]);

  useEffect(() => {
    let cancelled = false;
    const fetchVersions = async () => {
      if (!user) return;
      setUtilsVersions((prev) => ({ ...prev, loading: true, error: '' }));
      try {
        const payload = { month };
        const [kpisResp, chartsResp, tablesResp] = await Promise.all([
          getSeguimientoEjecutivoKpis(user, payload),
          getSeguimientoEjecutivoCharts(user, payload),
          getSeguimientoEjecutivoTables(user, payload),
        ]);
        if (cancelled) return;
        setUtilsVersions({
          loading: false,
          kpis: kpisResp?.meta?.utils_version || 'N/D',
          charts: chartsResp?.meta?.utils_version || 'N/D',
          tables: tablesResp?.meta?.utils_version || 'N/D',
          error: '',
        });
      } catch (error) {
        if (cancelled) return;
        setUtilsVersions({
          loading: false,
          kpis: 'N/D',
          charts: 'N/D',
          tables: 'N/D',
          error: error?.message || 'No se pudo consultar versión de utils',
        });
      }
    };
    fetchVersions();
    return () => {
      cancelled = true;
    };
  }, [user, month]);

  const handleDownloadTemplate = () => {
    const year = Number(month.slice(0, 4));
    const mon = Number(month.slice(5, 7));
    const templateRows = [
      ['RESPONSABLE', 'META_VOL_MES', 'ANIO', 'MES'],
      ['CAROLINA SALGADO', '130000000', String(year), String(mon)],
      ['JORGE MALDONADO', '147923958.12', String(year), String(mon)],
      ['VALERIA DIAZ PONCE', '573687739.45', String(year), String(mon)],
    ];
    const csvContent = templateRows.map((r) => r.join(';')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `plantilla_meta_volumen_${month}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const isCsv = /\.csv$/i.test(file.name || '');
      let json = [];
      if (isCsv) {
        // Para CSV evitamos auto-tipado de XLSX (que transforma 212736123,9 -> 2127361239).
        const text = await file.text();
        json = parseCsvSemicolon(text);
      } else {
        const bytes = await file.arrayBuffer();
        const wb = XLSX.read(bytes, { type: 'array' });
        const firstSheetName = wb.SheetNames[0];
        const ws = wb.Sheets[firstSheetName];
        json = XLSX.utils.sheet_to_json(ws, { defval: '' });
      }

      if (!Array.isArray(json) || json.length === 0) {
        setRows([]);
        setInvalidRows([]);
        setMessage('La primera hoja no contiene datos.');
        setMessageType('error');
        return;
      }

      const headers = Object.keys(json[0] || {});
      const responsableHeader = findHeader(headers, ['RESPONSABLE', 'RESPONSABLE MGC', 'RESPONSABLE_MGC', 'EJECUTIVO']);
      const metaHeader = findHeader(headers, ['META_VOL_MES', 'META VOL MES', 'META', 'META_MONTO', 'META MONTO', 'VOLUMEN ASIGNADO']);
      const anioHeader = findHeader(headers, ['ANIO', 'AÑO', 'ANIO_MET', 'ANO']);
      const mesHeader = findHeader(headers, ['MES', 'MES_MET']);

      if (!responsableHeader || !metaHeader) {
        setRows([]);
        setInvalidRows([]);
        setMessage('No se encontraron columnas obligatorias. Debe existir RESPONSABLE (o equivalente) y META (o equivalente).');
        setMessageType('error');
        return;
      }

      const fallbackYear = Number(month.slice(0, 4));
      const fallbackMonth = Number(month.slice(5, 7));

      const nextValid = [];
      const nextInvalid = [];

      json.forEach((r, idx) => {
        const responsable = String(r[responsableHeader] || '').trim();
        const meta = toNumber(r[metaHeader]);
        const anio = Number(anioHeader ? r[anioHeader] : fallbackYear) || fallbackYear;
        const mes = Number(mesHeader ? r[mesHeader] : fallbackMonth) || fallbackMonth;

        if (!responsable) {
          nextInvalid.push({ index: idx + 2, reason: 'RESPONSABLE vacío' });
          return;
        }
        if (meta == null || meta < 0) {
          nextInvalid.push({ index: idx + 2, responsable, reason: 'META inválida o negativa' });
          return;
        }
        if (!Number.isInteger(anio) || !Number.isInteger(mes) || mes < 1 || mes > 12) {
          nextInvalid.push({ index: idx + 2, responsable, reason: 'AÑO/MES inválido' });
          return;
        }

        nextValid.push({
          responsable,
          meta_vol_mes: meta,
          anio,
          mes,
        });
      });

      setRows(nextValid);
      setInvalidRows(nextInvalid);
      setLastResult(null);
      setMessage(`Archivo cargado: ${nextValid.length} filas válidas${nextInvalid.length ? `, ${nextInvalid.length} inválidas` : ''}.`);
      setMessageType(nextValid.length ? 'success' : 'error');
    } catch (error) {
      setRows([]);
      setInvalidRows([]);
      setMessage(`No se pudo leer el archivo: ${error?.message || 'error desconocido'}`);
      setMessageType('error');
    }
  };

  const enviar = async (op) => {
    if (!canSubmit) return;
    try {
      setLoading(true);
      setLastResult(null);

      if (op === 'apply' && rows.length > APPLY_CHUNK_SIZE) {
        const totalChunks = Math.ceil(rows.length / APPLY_CHUNK_SIZE);
        const aggregate = {
          ok: true,
          mode: 'apply_chunked',
          updated_rows: 0,
          matched_responsables: 0,
          invalid_rows: [],
          missing_responsables: [],
          touched_months: new Set(),
          nombre_cam_filter: nombreCam || null,
          meta_totals: {
            total_meta_archivo_valido: 0,
            total_meta_responsables_match: 0,
            total_meta_responsables_sin_match: 0,
          },
          chunk_results: [],
        };

        for (let i = 0; i < rows.length; i += APPLY_CHUNK_SIZE) {
          const chunk = rows.slice(i, i + APPLY_CHUNK_SIZE);
          const chunkNum = Math.floor(i / APPLY_CHUNK_SIZE) + 1;
          setMessage(`Aplicando carga... bloque ${chunkNum}/${totalChunks}`);
          setMessageType('success');

          const result = await cargarMetaVolumenMensual(user, {
            op: 'apply',
            month,
            nombre_cam: nombreCam || undefined,
            rows: chunk,
          });

          if (!result?.ok) {
            throw new Error(result?.error || `Falló bloque ${chunkNum}/${totalChunks}`);
          }

          aggregate.build_tag = aggregate.build_tag || result.build_tag;
          aggregate.parser_info = aggregate.parser_info || result.parser_info;
          aggregate.updated_rows += Number(result.updated_rows || 0);
          aggregate.matched_responsables += Number(result.matched_responsables || 0);
          aggregate.invalid_rows.push(...(Array.isArray(result.invalid_rows) ? result.invalid_rows : []));
          aggregate.missing_responsables.push(...(Array.isArray(result.missing_responsables) ? result.missing_responsables : []));
          (Array.isArray(result.touched_months) ? result.touched_months : []).forEach((m) => aggregate.touched_months.add(m));
          aggregate.meta_totals.total_meta_archivo_valido += Number(result?.meta_totals?.total_meta_archivo_valido || 0);
          aggregate.meta_totals.total_meta_responsables_match += Number(result?.meta_totals?.total_meta_responsables_match || 0);
          aggregate.meta_totals.total_meta_responsables_sin_match += Number(result?.meta_totals?.total_meta_responsables_sin_match || 0);
          aggregate.chunk_results.push({
            chunk: chunkNum,
            rows: chunk.length,
            updated_rows: Number(result.updated_rows || 0),
            matched_responsables: Number(result.matched_responsables || 0),
          });
        }

        const finalResult = {
          ...aggregate,
          touched_months: Array.from(aggregate.touched_months),
          invalid_rows: aggregate.invalid_rows.slice(0, 100),
          missing_responsables: aggregate.missing_responsables.slice(0, 200),
          audit: {
            executed_at: new Date().toISOString(),
            requested_by: user?.email || 'unknown',
            rows_received: rows.length,
            chunks: Math.ceil(rows.length / APPLY_CHUNK_SIZE),
          },
        };

        setLastResult(finalResult);
        setMessage('Carga aplicada correctamente en bloques.');
        setMessageType('success');
      } else {
        const result = await cargarMetaVolumenMensual(user, {
          op,
          month,
          nombre_cam: nombreCam || undefined,
          rows,
        });
        setLastResult(result);
        setMessage(op === 'preview' ? 'Prevalidación completada.' : 'Carga aplicada correctamente.');
        setMessageType('success');
      }
    } catch (error) {
      setMessage(`Error en la carga: ${error?.message || 'error desconocido'}`);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-baseline gap-4 mb-6 flex-wrap">
        <h1 className="text-2xl font-bold text-gray-800">Carga Meta Volumen Mensual</h1>
        <p className="text-gray-600">Carga manual desde Excel para fijar meta por responsable y mes</p>
        <Link
          to="/panel-marketing/mantenedor-subgerentes"
          className="btn-primary text-sm py-2 px-3"
          style={{ textDecoration: 'none' }}
        >
          Mantenedor subgerente
        </Link>
      </div>

      <div className="seccion-datos">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mes objetivo</label>
            <input
              type="month"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre campaña (opcional)</label>
            <input
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              value={nombreCam}
              onChange={(e) => setNombreCam(e.target.value)}
              placeholder="Filtra a una campaña específica"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Archivo</label>
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileUpload}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            <button
              type="button"
              className="mt-2 text-sm px-3 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 text-gray-700"
              onClick={handleDownloadTemplate}
            >
              Descargar plantilla CSV
            </button>
          </div>
        </div>

        {message && (
          <div className={`mt-4 p-3 rounded ${messageType === 'success' ? 'bg-green-100 text-green-700 border border-green-300' : 'bg-red-100 text-red-700 border border-red-300'}`}>
            {message}
          </div>
        )}

        <div className="mt-4 p-3 rounded border border-blue-200 bg-blue-50 text-blue-900 text-xs">
          <strong>Versión utils seguimiento:</strong>{' '}
          {utilsVersions.loading
            ? 'Consultando...'
            : `KPI: ${utilsVersions.kpis} | Charts: ${utilsVersions.charts} | Tables: ${utilsVersions.tables}`}
          {utilsVersions.error ? ` | Error: ${utilsVersions.error}` : ''}
        </div>

        {rows.length > 0 && (
          <>
            <div className="mt-4 flex flex-wrap gap-2">
              <button type="button" className="btn-primary" disabled={!canSubmit} onClick={() => enviar('preview')}>
                {loading ? 'Procesando...' : 'Validar (preview)'}
              </button>
              <button type="button" className="btn-primary" disabled={!canSubmit} onClick={() => enviar('apply')}>
                {loading ? 'Aplicando...' : 'Aplicar carga'}
              </button>
            </div>

            <div className="mt-4 tabla-container" style={{ overflowX: 'auto', maxHeight: '360px', overflowY: 'auto' }}>
              <table className="w-full" style={{ minWidth: '640px', fontSize: '0.875rem' }}>
                <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                  <tr>
                    <th style={{ backgroundColor: 'var(--if-primary)', color: 'white' }}>Responsable</th>
                    <th style={{ backgroundColor: 'var(--if-primary)', color: 'white' }}>Año</th>
                    <th style={{ backgroundColor: 'var(--if-primary)', color: 'white' }}>Mes</th>
                    <th style={{ backgroundColor: 'var(--if-primary)', color: 'white' }}>Meta Volumen</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 200).map((r, i) => (
                    <tr key={`${r.responsable}-${r.anio}-${r.mes}-${i}`}>
                      <td>{r.responsable}</td>
                      <td style={{ textAlign: 'center' }}>{r.anio}</td>
                      <td style={{ textAlign: 'center' }}>{r.mes}</td>
                      <td style={{ textAlign: 'right' }}>{Number(r.meta_vol_mes).toLocaleString('es-CL')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {invalidRows.length > 0 && (
          <div className="mt-4 text-xs bg-red-50 border border-red-200 rounded p-3">
            <strong>Filas inválidas (muestra):</strong>
            <pre className="mt-2 overflow-auto">{JSON.stringify(invalidRows.slice(0, 30), null, 2)}</pre>
          </div>
        )}

        {lastResult && (
          <pre className="mt-4 text-xs bg-gray-50 border border-gray-200 rounded p-3 overflow-auto">
            {JSON.stringify(lastResult, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
};

export default CargaMetaVolumenMensual;

